import React, { useState, useContext, useMemo } from 'react';
import { SessionContext } from '../../common/SessionContext';
import axios from 'axios';
import { Link } from 'react-router-dom';

/**
 * ResidencyBookingForm
 * --------------------
 * - Renders a list of available residency weeks as checkboxes (1‑6 items).
 * - `totalPrice` updates automatically according to how many boxes are ticked.
 * - Uses `useMemo` for all derived data so work happens only when dependencies change.
 */

function ResidencyBookingForm({ residencies }) {
    /* ------------------------------------------------------------------
     * Context & constants
     * ----------------------------------------------------------------*/
    const { session } = useContext(SessionContext);
    const currentCurrency = session.currency?.toLowerCase();

    const CURRENCY_SYMBOLS = { eur: '€', gbp: '£', usd: '$' };
    const currencySymbol = CURRENCY_SYMBOLS[currentCurrency] ?? '';

    /* ------------------------------------------------------------------
     * Price table (1–6 weeks)
     * ----------------------------------------------------------------*/
    const prices = useMemo(() => {
        if (!residencies) return {};
        const pricing = residencies.pricing?.[currentCurrency] || {};
        return {
            1: pricing[1]?.full_amount ?? 0,
            2: pricing[2]?.full_amount ?? 0,
            3: pricing[3]?.full_amount ?? 0,
            4: pricing[4]?.full_amount ?? 0,
            5: pricing[5]?.full_amount ?? 0,
            6: pricing[6]?.full_amount ?? 0,
        };
    }, [residencies, currentCurrency]);

    const fullpriceids = useMemo(() => {
        if (!residencies) return {};
        const pricing = residencies.pricing?.[currentCurrency] || {};
        return {
            1: pricing[1]?.stripe_full_price_id ?? 0,
            2: pricing[2]?.stripe_full_price_id ?? 0,
            3: pricing[3]?.stripe_full_price_id ?? 0,
            4: pricing[4]?.stripe_full_price_id ?? 0,
            5: pricing[5]?.stripe_full_price_id ?? 0,
            6: pricing[6]?.stripe_full_price_id ?? 0,
        };
    }, [residencies, currentCurrency]);

    const subpriceids = useMemo(() => {
        if (!residencies) return {};
        const pricing = residencies.pricing?.[currentCurrency] || {};
        return {
            1: pricing[1]?.stripe_sub_price_id ?? 0,
            2: pricing[2]?.stripe_sub_price_id ?? 0,
            3: pricing[3]?.stripe_sub_price_id ?? 0,
            4: pricing[4]?.stripe_sub_price_id ?? 0,
            5: pricing[5]?.stripe_sub_price_id ?? 0,
            6: pricing[6]?.stripe_sub_price_id ?? 0,
        };
    }, [residencies, currentCurrency]);

    /* ------------------------------------------------------------------
     * Form state
     * ----------------------------------------------------------------*/
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [weekstring, setWeekstring] = useState('');
    const [selectedWeekIds, setSelectedWeekIds] = useState(() => new Set());

    const selectedCount = selectedWeekIds.size;

    // 💰 compute total based on how many weeks are ticked (0…6)
    const totalPrice = useMemo(() => prices[selectedCount] ?? 0, [prices, selectedCount]);
    const fullPriceCode = useMemo(() => fullpriceids[selectedCount] ?? 0, [fullpriceids, selectedCount]);
    const subPriceCode = useMemo(() => subpriceids[selectedCount] ?? 0, [subpriceids, selectedCount]);

    /* ------------------------------------------------------------------
     * Checkbox helper
     * ----------------------------------------------------------------*/
    const handleCheckboxChange = (e, id) => {
        setSelectedWeekIds(prev => {
            const next = new Set(prev);
            if (e.target.checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            setWeekstring(Array.from(next).join());
            return next;
        });
    };

    /* ------------------------------------------------------------------
     * Render list of weeks
     * ----------------------------------------------------------------*/
    const weekCheckboxes = useMemo(() => {
        if (!residencies?.available_weeks) return <p>Loading weeks…</p>;
        return residencies.available_weeks.map(w => (
            <div className="form-check" key={w.id}>
                <input
                    type="checkbox"
                    id={`rw-${w.id}`}
                    className="form-check-input"
                    checked={selectedWeekIds.has(w.id)}
                    onChange={e => handleCheckboxChange(e, w.id)}
                />
                <label htmlFor={`rw-${w.id}`} className="form-check-label">
                    {w.start_date} <i>({w.remaining} places left)</i>
                </label>
            </div>
        ));
    }, [residencies, selectedWeekIds]);

    /* ------------------------------------------------------------------
     * JSX
     * ----------------------------------------------------------------*/
    return (
        <aside className="package-widget-style-2 widget-form">
            <div className="text-center mb-3">
                <div className="res-book-title">
                    <h4>Book Now</h4>
                </div>
            </div>

            <div className="widget-body">
                <p className="booktext">
                    Select one or more consecutive weeks below. If you need non‑consecutive dates, please make separate bookings.
                </p>

                <form id="booking-form">
                    {/* ▸ Personal details */}
                    <div className="booking-form-wrapper">
                        <div className="custom-input-group">
                            <input
                                type="text"
                                placeholder="Your first name"
                                id="first_name"
                                value={firstName}
                                onChange={e => setFirstName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="custom-input-group">
                            <input
                                type="text"
                                placeholder="Your last name"
                                id="last_name"
                                value={lastName}
                                onChange={e => setLastName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="custom-input-group">
                            <input
                                type="email"
                                placeholder="Your email"
                                id="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                            />
                        </div>

                        {/* ▸ Week selection */}
                        <div className="residency-date-checks mb-3">
                            <p className="font-semibold">Select your dates:</p>
                            {weekCheckboxes}
                        </div>

                        {/* ▸ Total price */}
                        <div className="custom-input-group total-price mb-3 text-lg">
                            <strong>Total Price: {currencySymbol}{totalPrice}</strong>
                        </div>

                    </div>

                    <div style={{ display: "flex", gap: "10px" }}>

                      <form action="https://www.campfr.com/api/residency_checkout" method="POST">
                        <input type="hidden" name="paymentType" value="payment" />
                        <input type="hidden" name="amount" value={totalPrice} />
                        <input type="hidden" name="currency" value={currentCurrency} />
                        <input type="hidden" name="priceCode" value={fullPriceCode} />
                        <input type="hidden" name="firstname" value={firstName} />
                        <input type="hidden" name="lastname" value={lastName} />
                        <input type="hidden" name="email" value={email} />
                        <input type="hidden" name="weekstring" value={weekstring} />
                        <input type="hidden" name="ws" value={session.ws} />
                        <button className="confirm-process-button" type="submit">
                          Pay in full
                        </button>
                      </form>

                      <form action="https://www.campfr.com/api/residency_checkout" method="POST">
                        <input type="hidden" name="paymentType" value="subscription" />
                        <input type="hidden" name="amount" value={totalPrice/4} />
                        <input type="hidden" name="currency" value={currentCurrency} />
                        <input type="hidden" name="priceCode" value={subPriceCode} />
                        <input type="hidden" name="firstname" value={firstName} />
                        <input type="hidden" name="lastname" value={lastName} />
                        <input type="hidden" name="email" value={email} />
                        <input type="hidden" name="weekstring" value={weekstring} />
                        <input type="hidden" name="ws" value={session.ws} />
                        <button className="confirm-process-button" type="submit">
                          Split over 4 months
                        </button>
                      </form>

                    </div>


                </form>
            </div>
        </aside>
    );
}

export default ResidencyBookingForm;
